/**
 * Детали заказа для клиента.
 */

import { formatRub as formatPrice } from '../../../shared/formatMoney.js';

const PAYMENT_STATUS_LABELS = {
  pending: 'Ожидает оплаты',
  processing: 'Оплачено',
  succeeded: 'Оплачено',
  failed: 'Ошибка оплаты',
  refunded: 'Средства возвращены',
  cancelled: 'Оплата отменена',
};

const getPaymentStatusLabel = (paymentStatus, orderType) => {
  if (paymentStatus === 'refunded' && orderType === 'booking_no_prepayment') {
    return 'Оплата отменена';
  }
  return PAYMENT_STATUS_LABELS[paymentStatus] || paymentStatus;
};

const PAYMENT_STATUS_COLORS = {
  pending: 'text-yellow-600 dark:text-yellow-400',
  processing: 'text-blue-600 dark:text-blue-400',
  succeeded: 'text-green-600 dark:text-green-400',
  failed: 'text-red-600 dark:text-red-400',
  refunded: 'text-orange-600 dark:text-orange-400',
  cancelled: 'text-gray-600 dark:text-gray-400',
};

const STATUS_BADGE_COLORS = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  pending_payment: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  pending_confirmation: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  confirmed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  ready: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  refunded: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
};

export function OrderDetails({ order }) {
  /**
   * Форматирует дату и время (для created_at, cancelled_at).
   * Пример: "10 марта 2026, 15:30"
   */
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    
    // Дата с названием месяца
    const dateFormatted = date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    
    // Время
    const timeFormatted = date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });
    
    return `${dateFormatted}, ${timeFormatted}`;
  };

  /**
   * Форматирует дату бронирования.
   * Пример: "15 марта 2026"
   */
  const formatBookingDate = (dateStr) => {
    if (!dateStr) return '-';
    
    // Парсим дату в формате ISO (YYYY-MM-DD)
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  /**
   * Форматирует время бронирования.
   * Пример: "19:00"
   */
  const formatBookingTime = (timeStr) => {
    if (!timeStr) return '-';
    
    // Убираем секунды из времени (HH:MM:SS -> HH:MM)
    const [hours, minutes] = timeStr.split(':');
    return `${hours}:${minutes}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
      {/* Header — номер заказа слева, статус справа */}
      <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-700 dark:to-purple-700">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-indigo-100 text-sm">Заказ</p>
            <h1 className="text-2xl font-bold text-white">#{order.id}</h1>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-medium ${STATUS_BADGE_COLORS[order.status] || STATUS_BADGE_COLORS.draft}`}>
            {order.status_display}
          </span>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Заведение */}
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center flex-shrink-0">
            <span className="text-lg">📍</span>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Заведение</p>
            <p className="font-semibold text-gray-900 dark:text-white">{order.company_name}</p>
            {order.company_address && (
              <p className="text-sm text-gray-600 dark:text-gray-400">{order.company_address}</p>
            )}
          </div>
        </div>

        {/* Причина отмены (если заказ отменён) */}
        {order.status === 'cancelled' && order.cancel_reason && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-lg">❌</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">
                  Причина отмены
                </p>
                <p className="text-sm text-red-700 dark:text-red-400">
                  {order.cancel_reason}
                </p>
                {order.cancelled_at && (
                  <p className="text-xs text-red-600 dark:text-red-500 mt-2">
                    Отменён {formatDateTime(order.cancelled_at)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Комментарий клиента */}
        {/* {order.comment && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
                <span className="text-sm">💬</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">
                  Комментарий к заказу
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {order.comment}
                </p>
              </div>
            </div>
          </div>
        )} */}

        {/* Желаемое время получения (для покупок) */}
        {order.order_type === 'purchase' && (order.pickup_date || order.pickup_time) && (
          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-4">
            <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-3 flex items-center gap-2">
              <span>⏰</span> Желаемое время получения
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {order.pickup_date && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-xs uppercase">Дата</p>
                  <p className="font-bold text-lg">{formatBookingDate(order.pickup_date)}</p>
                </div>
              )}
              {order.pickup_time && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-xs uppercase">Время</p>
                  <p className="font-bold text-lg">{formatBookingTime(order.pickup_time)}</p>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Бронирование */}
        {order.booking_details && (
          <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-xl p-4">
            <h3 className="font-semibold text-indigo-900 dark:text-indigo-100 mb-3 flex items-center gap-2">
              <span>📅</span> Бронирование
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                <p className="text-gray-500 dark:text-gray-400 text-xs uppercase">Дата</p>
                <p className="font-bold text-lg">{formatBookingDate(order.booking_details.booking_date)}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                <p className="text-gray-500 dark:text-gray-400 text-xs uppercase">Время</p>
                <p className="font-bold text-lg">{formatBookingTime(order.booking_details.booking_time)}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center col-span-2">
                <p className="text-gray-500 dark:text-gray-400 text-xs uppercase">Гостей</p>
                <p className="font-bold text-lg">{order.booking_details.guests_count}</p>
              </div>
              {order.booking_details.full_deposit_amount > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center col-span-2">
                  <p className="text-gray-500 dark:text-gray-400 text-xs uppercase">Депозит</p>
                  <p className="font-bold text-lg">{formatPrice(order.booking_details.full_deposit_amount)}</p>
                </div>
              )}
              {order.booking_details.deposit_description && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 col-span-2">
                  <p className="text-gray-500 dark:text-gray-400 text-xs uppercase">Описание депозита</p>
                  <p className="font-medium text-gray-900 dark:text-white">{order.booking_details.deposit_description}</p>
                </div>
              )}
            </div>
            {order.booking_details.comment && (
              <div className="mt-3 pt-3 border-t border-indigo-200 dark:border-indigo-800">
                <div className="flex items-start gap-2">
                  <span className="text-sm">💬</span>
                  <div>
                    <p className="text-xs font-semibold text-indigo-800 dark:text-indigo-300 mb-1">Пожелания</p>
                    <p className="text-sm text-indigo-700 dark:text-indigo-400">{order.booking_details.comment}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Универсальный заказ — состав */}
        {order.order_type === 'universal' && order.client_info && (
          <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-xl p-4 space-y-3">
            <h3 className="font-semibold text-indigo-900 dark:text-indigo-100 flex items-center gap-2">
              <span>📋</span> Состав заказа
            </h3>
            <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-100">
              {order.client_info}
            </p>
          </div>
        )}

        {/* Желаемое время получения (для покупок) */}
        {order.order_type === 'purchase' && (order.pickup_date || order.pickup_time) && (
          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-4">
            <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-3 flex items-center gap-2">
              <span>⏰</span> Желаемое время получения
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {order.pickup_date && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-xs uppercase">Дата</p>
                  <p className="font-bold text-lg">{formatBookingDate(order.pickup_date)}</p>
                </div>
              )}
              {order.pickup_time && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-xs uppercase">Время</p>
                  <p className="font-bold text-lg">{formatBookingTime(order.pickup_time)}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Товары */}
        {order.order_type !== 'universal' && order.items && order.items.length > 0 && (
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <span>🛒</span> Товары
            </h3>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl overflow-hidden">
              {order.items.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex gap-3 items-center p-4 ${
                    index !== order.items.length - 1
                      ? 'border-b border-gray-200 dark:border-gray-600'
                      : ''
                  }`}
                >
                  {item.product_image ? (
                    <img
                      src={item.product_image}
                      alt={item.product_name}
                      className="w-14 h-14 object-cover rounded-lg flex-shrink-0 bg-gray-200 dark:bg-gray-600"
                    />
                  ) : (
                    <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-gray-200 dark:bg-gray-600 flex-shrink-0">
                      <span className="text-xl">📦</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{item.product_name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">× {item.quantity}</p>
                  </div>
                  <p className="font-semibold flex-shrink-0">{formatPrice(item.total_price)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Итого — только если есть товары или депозит */}
        {(order.items?.length > 0 || order.booking_details?.deposit_amount || order.order_type === 'universal') && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-2">
            {/* Для бронирования с депозитом: показываем полную сумму депозита */}
            {order.order_type === 'universal' ? (
              <>
                {order.original_amount > 0 && order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>Стоимость без скидки:</span>
                    <span className="line-through">{formatPrice(order.original_amount)}</span>
                  </div>
                )}
                {order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>🏷 Скидка:</span>
                    <span className="text-green-600 dark:text-green-400">−{formatPrice(order.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Итого:</span>
                  <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {formatPrice(order.total_amount)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                  <span>К оплате онлайн:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{formatPrice(order.amount_to_pay)}</span>
                </div>
                {order.paid_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>💳 Оплачено:</span>
                    <span className="text-green-600 dark:text-green-400">{formatPrice(order.paid_amount)}</span>
                  </div>
                )}
              </>
            ) : order.order_type === 'booking_with_prepayment' && order.booking_details?.full_deposit_amount ? (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Депозит:</span>
                  <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {formatPrice(order.booking_details.full_deposit_amount)}
                  </span>
                </div>
                {order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>🏷 Скидка:</span>
                    <span className="text-green-600 dark:text-green-400">−{formatPrice(order.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                  <span>💳 Оплачено:</span>
                  <span className="text-green-600 dark:text-green-400">{formatPrice(order.paid_amount || order.total_amount)}</span>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  На месте вы получите услуг на {formatPrice(order.booking_details.full_deposit_amount)}
                </p>
              </>
            ) : (
              <>
                {/* Для покупок со скидкой */}
                {order.original_amount > 0 && order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>Цена:</span>
                    <span className="line-through">{formatPrice(order.original_amount)}</span>
                  </div>
                )}
                {order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>🏷 Скидка:</span>
                    <span className="text-green-600 dark:text-green-400">−{formatPrice(order.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Итого:</span>
                  <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {formatPrice(order.total_amount)}
                  </span>
                </div>
              </>
            )}
            {order.payment_status?.has_payment && (
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                <p className="text-sm">
                  💳 Статус оплаты:{' '}
                  <span className={PAYMENT_STATUS_COLORS[order.payment_status.status] || 'text-gray-600 dark:text-gray-400'}>
                    {getPaymentStatusLabel(order.payment_status.status, order.order_type)}
                  </span>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Дата создания */}
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          Создан {formatDateTime(order.created_at)}
        </div>
      </div>
    </div>
  );
}
